'use client'

import { Card, CardBody } from '@heroui/card'
import { Image } from '@heroui/image'
import { getLocalTimeZone, today } from '@internationalized/date'
import { useQuery } from '@tanstack/react-query'
import NextImage from 'next/image'
import { useRouter } from 'next/navigation'
import { useState } from 'react'
import { useEffect, useMemo, useCallback } from 'react'

import LetterIllustration from '@/assets/svgs/moment-letter.svg'
import SailIllustration from '@/assets/webp/sail.webp'
import { AlertCircleIcon, Stack } from '@/components/shared'
import { Typography } from '@/components/ui'
import { useImage } from '@/data'
import { env } from '@/env'
import { useDecrypt } from '@/hooks'
import { routes } from '@/routes'
import { useHomeStore, useEncryptionKey } from '@/store'
import { useApi } from '@/trpc/react'
import { truncateText } from '@/utils'

const hashCode = (str: string) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash)
}

const FavoriteContent = ({ content }: { content: string }) => {
  const { data: decryptedContent, isLoading } = useDecrypt({
    enabled: !!content,
    ciphertext: content,
  })
  if (isLoading) return null
  return (
    <div className="flex h-full w-full flex-col border-3 border-white bg-[#FFF7ED] px-6 py-5">
      <Typography className="font-crimsonPro text-[10px] font-light text-black/90">
        {truncateText(decryptedContent ?? '', 200)}
      </Typography>
    </div>
  )
}

const FavoriteImage = (image: { path: string; type: string }) => {
  const { data: imageData, isLoading } = useImage({
    enabled: !!image.path,
    image,
  })
  if (isLoading) return null
  return (
    <div className="h-full w-full overflow-clip rounded-[36px] border-3 border-white">
      <Image
        src={imageData?.url}
        alt="Favorite Image"
        className="pointer-events-none h-[151px] w-[183px] object-cover"
      />
    </div>
  )
}

const DailyFavorites = () => {
  const api = useApi()
  const router = useRouter()
  const { favorites, favoriteDate, setFavorites } = useHomeStore()
  const encryptionKey = useEncryptionKey()
  const [mounted, setMounted] = useState(false)

  const shouldGetNewDailyFavorites = useMemo(() => {
    if (!mounted) return false
    if (!favorites?.length || !favoriteDate) return true
    const today = new Date()
    const favoriteDay = new Date(favoriteDate)
    today.setHours(0, 0, 0, 0)
    favoriteDay.setHours(0, 0, 0, 0)
    // If the site is not in production, always get new favorites for testing
    if (env.NEXT_PUBLIC_VERCEL_ENV !== 'production') return true
    return favoriteDay < today
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoriteDate, mounted])

  const localTimeZone = getLocalTimeZone()
  const startOfTodayInUTC = today(localTimeZone)
    .toDate(localTimeZone)
    .toISOString()
  const endOfTodayInUTC = today(localTimeZone)
    .add({ days: 1 })
    .toDate(localTimeZone)
    .toISOString()

  const { data: futureLetters } = useQuery({
    ...api.moments.getLettersByRangeDate.queryOptions({
      startDate: startOfTodayInUTC,
      endDate: endOfTodayInUTC,
    }),
    enabled: shouldGetNewDailyFavorites,
  })

  const futureLetter = futureLetters?.[0]

  const handleFutureLetterClick = () => {
    router.push(`${routes.detailMoment(futureLetter?.id ?? '')}`)
  }

  const { data: dailyFavorites, isLoading } = useQuery(
    api.moments.getRandomFavorites.queryOptions(
      {
        limit: 5,
      },
      {
        enabled: shouldGetNewDailyFavorites,
      },
    ),
  )

  const handleClick = useCallback(
    (momentId: string) => {
      router.push(`${routes.detailMoment(momentId)}`)
    },
    [router],
  )

  const cardsData = useMemo(() => {
    if (!favorites?.length) return []
    return favorites?.map((favorite) => {
      const hash = hashCode(favorite.id)
      const hasImage = favorite.momentImages.length > 0
      const randomImage = hasImage
        ? favorite.momentImages[hash % favorite.momentImages.length]
        : null
      const content = favorite.momentEncrypted
      const shouldShowContent = !hasImage || (hasImage && hash % 100 < 1)
      return {
        id: favorite.id,
        onCardClick: () => handleClick(favorite.id),
        content: shouldShowContent ? (
          <FavoriteContent content={content} />
        ) : (
          <FavoriteImage
            path={randomImage?.original ?? ''}
            type={randomImage?.type ?? ''}
          />
        ),
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favorites, handleClick, encryptionKey])

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (dailyFavorites && shouldGetNewDailyFavorites) {
      setFavorites(dailyFavorites)
    }
  }, [dailyFavorites, setFavorites, shouldGetNewDailyFavorites])

  if (isLoading || !favorites?.length || !mounted || cardsData.length === 0) {
    return (
      <Card
        shadow="none"
        className={`col-span-1 rounded-[36px] border-3 border-white bg-primary-50`}
      >
        <CardBody className="flex flex-col items-center justify-center gap-3 p-5">
          <Typography
            size="xsmall"
            className="text-default-foregroun text-center font-crimsonPro"
          >
            No memories to display
          </Typography>
          <Typography
            size="xsmall"
            className="text-default-foregroun text-center font-crimsonPro"
          >
            Special moments will appear here eventually ✨
          </Typography>
        </CardBody>
      </Card>
    )
  }

  if (!!futureLetter) {
    return (
      <div
        className="relative mx-auto h-[151px] w-[182px]"
        onClick={handleFutureLetterClick}
      >
        <NextImage src={LetterIllustration} alt="Install Art" width={182} />
        <NextImage
          src={SailIllustration}
          alt="Install Art"
          width={38}
          className="absolute left-[72px] top-[59px]"
        />
        <AlertCircleIcon className="absolute right-px top-px rotate-6 transform text-2xl text-danger-600" />
      </div>
    )
  }

  return (
    <div className="relative mx-auto h-[151px] w-[183px]">
      <Stack
        cardsData={cardsData ?? []}
        cardDimensions={{ width: 183, height: 151 }}
        randomRotation
        // showIndicators
      />
    </div>
  )
}

export default DailyFavorites
